import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = axios.create({
  baseURL: "http://localhost:5000/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

export default function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const loadUsers = async () => {
    try {
      setLoading(true);
      setMessage("");

      const res = await API.get("/auth/users");

      if (res.data.success) {
        setUsers(res.data.users || []);
      }
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const giveInternalAccess = async (userId) => {
    try {
      setActionLoadingId(userId);
      setMessage("");

      const res = await API.patch(`/auth/users/${userId}/internal-access`);
      setMessage(res.data.message || "Internal access granted");

      await loadUsers();
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || "Failed to grant access");
    } finally {
      setActionLoadingId(null);
    }
  };

  const removeInternalAccess = async (userId) => {
    try {
      setActionLoadingId(userId);
      setMessage("");

      const res = await API.patch(`/auth/users/${userId}/remove-internal-access`);
      setMessage(res.data.message || "Internal access removed");

      await loadUsers();
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || "Failed to remove access");
    } finally {
      setActionLoadingId(null);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const stats = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => u.role === "admin").length;
    const internal = users.filter((u) => u.isInternalTeam).length;
    const clients = users.filter((u) => u.role === "client").length;

    return { total, admins, internal, clients };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase().trim();

    return users.filter((user) => {
      const textMatch =
        !q ||
        String(user.name || "").toLowerCase().includes(q) ||
        String(user.email || "").toLowerCase().includes(q) ||
        String(user.companyName || "").toLowerCase().includes(q) ||
        String(user.mobileNumber || "").toLowerCase().includes(q);

      let filterMatch = true;

      if (filter === "admin") filterMatch = user.role === "admin";
      if (filter === "internal") filterMatch = !!user.isInternalTeam;
      if (filter === "client") filterMatch = user.role === "client";
      if (filter === "normal") {
        filterMatch = user.role === "client" && !user.isInternalTeam;
      }

      return textMatch && filterMatch;
    });
  }, [users, search, filter]);

  const styles = {
    page: {
      minHeight: "100vh",
      padding: "28px",
      background:
        "radial-gradient(circle at top left, rgba(37,99,235,0.28), transparent 35%), #020617",
      color: "#f8fafc",
    },
    shell: {
      maxWidth: "1280px",
      margin: "0 auto",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "22px",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      borderRadius: "999px",
      background: "rgba(59,130,246,0.14)",
      border: "1px solid rgba(147,197,253,0.35)",
      color: "#bfdbfe",
      fontWeight: 800,
      fontSize: "12px",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    title: {
      fontSize: "34px",
      fontWeight: 900,
      margin: "12px 0 8px",
      letterSpacing: "-0.04em",
    },
    subtitle: {
      color: "#cbd5e1",
      maxWidth: "760px",
      lineHeight: 1.6,
      margin: 0,
    },
    refreshBtn: {
      border: "none",
      borderRadius: "14px",
      padding: "12px 16px",
      background: "#22c55e",
      color: "#052e16",
      fontWeight: 900,
      cursor: "pointer",
      boxShadow: "0 12px 28px rgba(34,197,94,0.22)",
    },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: "16px",
      marginBottom: "20px",
    },
    statCard: {
      background: "rgba(15,23,42,0.78)",
      border: "1px solid rgba(148,163,184,0.22)",
      borderRadius: "22px",
      padding: "18px",
      boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
    },
    statLabel: {
      color: "#94a3b8",
      fontSize: "13px",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    statValue: {
      fontSize: "34px",
      fontWeight: 900,
      marginTop: "8px",
    },
    controls: {
      display: "grid",
      gridTemplateColumns: "1fr 220px",
      gap: "14px",
      marginBottom: "18px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "16px",
      border: "1px solid rgba(148,163,184,0.28)",
      background: "rgba(15,23,42,0.9)",
      color: "#f8fafc",
      outline: "none",
      fontSize: "14px",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "16px",
      border: "1px solid rgba(148,163,184,0.28)",
      background: "#0f172a",
      color: "#f8fafc",
      outline: "none",
      fontSize: "14px",
      fontWeight: 700,
    },
    message: {
      marginBottom: "16px",
      padding: "14px 16px",
      borderRadius: "16px",
      background: "rgba(37,99,235,0.16)",
      border: "1px solid rgba(96,165,250,0.35)",
      color: "#dbeafe",
      fontWeight: 700,
    },
    card: {
      background: "rgba(15,23,42,0.86)",
      border: "1px solid rgba(148,163,184,0.22)",
      borderRadius: "24px",
      overflow: "hidden",
      boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
    },
    tableWrap: {
      overflowX: "auto",
    },
    table: {
      width: "100%",
      minWidth: "980px",
      borderCollapse: "collapse",
    },
    th: {
      textAlign: "left",
      padding: "15px 16px",
      borderBottom: "1px solid rgba(148,163,184,0.22)",
      color: "#93c5fd",
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      background: "rgba(2,6,23,0.55)",
    },
    td: {
      padding: "15px 16px",
      borderBottom: "1px solid rgba(148,163,184,0.12)",
      color: "#f8fafc",
      fontSize: "14px",
      verticalAlign: "middle",
    },
    userName: {
      fontWeight: 900,
      marginBottom: "4px",
    },
    muted: {
      color: "#94a3b8",
      fontSize: "12px",
    },
    pillAdmin: {
      padding: "6px 10px",
      borderRadius: "999px",
      background: "rgba(37,99,235,0.22)",
      color: "#bfdbfe",
      fontWeight: 900,
      fontSize: "12px",
      border: "1px solid rgba(147,197,253,0.28)",
    },
    pillClient: {
      padding: "6px 10px",
      borderRadius: "999px",
      background: "rgba(148,163,184,0.16)",
      color: "#e2e8f0",
      fontWeight: 900,
      fontSize: "12px",
      border: "1px solid rgba(148,163,184,0.2)",
    },
    pillInternal: {
      padding: "6px 10px",
      borderRadius: "999px",
      background: "rgba(16,185,129,0.18)",
      color: "#a7f3d0",
      fontWeight: 900,
      fontSize: "12px",
      border: "1px solid rgba(110,231,183,0.28)",
    },
    pillNormal: {
      padding: "6px 10px",
      borderRadius: "999px",
      background: "rgba(239,68,68,0.14)",
      color: "#fecaca",
      fontWeight: 900,
      fontSize: "12px",
      border: "1px solid rgba(248,113,113,0.28)",
    },
    actionBtn: {
      border: "none",
      borderRadius: "12px",
      padding: "10px 12px",
      background: "#2563eb",
      color: "#ffffff",
      fontWeight: 900,
      cursor: "pointer",
      minWidth: "120px",
    },
    removeBtn: {
      border: "none",
      borderRadius: "12px",
      padding: "10px 12px",
      background: "#dc2626",
      color: "#ffffff",
      fontWeight: 900,
      cursor: "pointer",
      minWidth: "120px",
    },
    disabledBtn: {
      opacity: 0.65,
      cursor: "not-allowed",
    },
    empty: {
      padding: "34px",
      textAlign: "center",
      color: "#cbd5e1",
    },
    note: {
      marginTop: "16px",
      padding: "16px",
      borderRadius: "18px",
      background: "rgba(245,158,11,0.12)",
      border: "1px solid rgba(251,191,36,0.25)",
      color: "#fde68a",
      lineHeight: 1.6,
    },
  };

  const StatCard = ({ label, value }) => (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div>
            <div style={styles.badge}>Admin Control Center</div>
            <h1 style={styles.title}>User Access Management</h1>
            <p style={styles.subtitle}>
              Manage who gets internal team access. Internal team users get full
              dashboard, upload, report, export, and print access without payment,
              but they do not get the admin control panel unless their role is admin.
            </p>
          </div>

          <button style={styles.refreshBtn} onClick={loadUsers} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh Users"}
          </button>
        </div>

        <div style={styles.statGrid}>
          <StatCard label="Total Users" value={stats.total} />
          <StatCard label="Admin Users" value={stats.admins} />
          <StatCard label="Internal Team" value={stats.internal} />
          <StatCard label="Client Users" value={stats.clients} />
        </div>

        <div style={styles.controls}>
          <input
            style={styles.input}
            placeholder="Search by name, email, company, or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            style={styles.select}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Users</option>
            <option value="admin">Admins Only</option>
            <option value="internal">Internal Team</option>
            <option value="client">Clients Only</option>
            <option value="normal">Normal Clients</option>
          </select>
        </div>

        {message ? <div style={styles.message}>{message}</div> : null}

        <div style={styles.card}>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Mobile</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Internal Access</th>
                  <th style={styles.th}>Joined</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td style={styles.empty} colSpan="7">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td style={styles.empty} colSpan="7">
                      No matching users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const isAdmin = user.role === "admin";
                    const isInternal = !!user.isInternalTeam;
                    const actionLoading = actionLoadingId === user.id;

                    return (
                      <tr key={user.id}>
                        <td style={styles.td}>
                          <div style={styles.userName}>{user.name || "-"}</div>
                          <div style={styles.muted}>{user.email || "-"}</div>
                          <div style={styles.muted}>ID: {user.id}</div>
                        </td>

                        <td style={styles.td}>{user.companyName || "-"}</td>

                        <td style={styles.td}>{user.mobileNumber || "-"}</td>

                        <td style={styles.td}>
                          <span style={isAdmin ? styles.pillAdmin : styles.pillClient}>
                            {isAdmin ? "ADMIN" : "CLIENT"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <span style={isInternal ? styles.pillInternal : styles.pillNormal}>
                            {isInternal ? "FULL ACCESS" : "TRIAL / PAID"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : "-"}
                        </td>

                        <td style={styles.td}>
                          {isAdmin ? (
                            <span style={styles.muted}>
                              Admin account
                            </span>
                          ) : isInternal ? (
                            <button
                              style={{
                                ...styles.removeBtn,
                                ...(actionLoading ? styles.disabledBtn : {}),
                              }}
                              disabled={actionLoading}
                              onClick={() => removeInternalAccess(user.id)}
                            >
                              {actionLoading ? "Updating..." : "Remove Access"}
                            </button>
                          ) : (
                            <button
                              style={{
                                ...styles.actionBtn,
                                ...(actionLoading ? styles.disabledBtn : {}),
                              }}
                              disabled={actionLoading}
                              onClick={() => giveInternalAccess(user.id)}
                            >
                              {actionLoading ? "Updating..." : "Give Access"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.note}>
          <strong>Access rule:</strong> Only role = admin can view this page.
          Internal team users with role = client get full dashboard access but cannot
          manage users.
        </div>
      </div>
    </div>
  );
}